import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../app/services/hooks/useOrganizationService';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import { AuthContext } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../app/models/Auth';
import PageHeader from '../../../components/PageHeader/PageHeader';
import DefaultCard from '../../../components/default/DefaultCard';
import DefaultModal from '../../../components/default/DefaultModal';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import ButtonTableAction from '../../../components/buttons/ButtonTableAction';
import InputField from '../../../components/InputField';
import SquareCheckbox from '../../../components/SquareCheckbox/SquareCheckbox';
import UsageBar from '../components/UsageBar';
import { PlanLimits, PlanUsage, formatDate } from '../types';

export interface PortfolioClient {
    id: number;
    client_id: number;
    name: string;
    first_name: string;
    last_name: string;
    second_last_name: string;
    email: string;
    phone: string;
    rut: string;
    account_active: boolean;
    status: 'active' | 'archived' | 'pending';
    status_display: string;
    added_by: string | null;
    created_at: string;
    brand_applications_count: number | null;
    contracts_count: number | null;
}

const EMPTY_FORM = { email: '', first_name: '', last_name: '', second_last_name: '', send_welcome: true };

const ClientList: React.FC = () => {
    const history = useHistory();
    const { auth } = useContext(AuthContext);
    const { getClients, storeClient, fetchingClients, fetchingStoreClient } = useOrganizationService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();

    const [clients, setClients] = useState<PortfolioClient[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<'active' | 'archived'>('active');
    const [limits, setLimits] = useState<PlanLimits | null>(null);
    const [usage, setUsage] = useState<PlanUsage | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);

    const perPage = 20;
    const canAdd = hasPermission(auth, 'add_organizationclient') && auth?.organization?.status === 'active';

    const load = (nextPage = page) => {
        getClients({ search, status, page: nextPage, per_page: perPage }, {
            onSuccess: (response) => {
                setClients(response.data.clients);
                setTotal(response.data.total);
                setLimits(response.data.limits);
                setUsage(response.data.usage);
            },
            onError: (response) => toast.error(response.message)
        });
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            load(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search, status]);

    const handleStore = () => {
        storeClient(form, {
            onSuccess: (response) => {
                toast.success(response.message);
                setErrors({});
                setShowForm(false);
                setForm(EMPTY_FORM);
                load(1);
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => {
                setErrors(response.data);
                toast.error('Revisa los datos del cliente');
            }
        });
    };

    const full = !!limits && !!usage && limits.max_clients !== -1 && usage.clients >= limits.max_clients;
    const pages = Math.max(1, Math.ceil(total / perPage));

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[{ label: 'Inicio', path: '/intranet' }, { label: 'Cartera de Clientes', isCurrent: true }]}
                title="Cartera de Clientes"
                description="Los clientes de tu estudio, con sus marcas y contratos. Solo los ve tu equipo."
            />

            <DefaultCard>
                <div className="row align-items-end mb-3">
                    <div className="col-md-4 mb-2">
                        <InputField label="Buscar" value={search} onChange={setSearch} icon="fa-search"
                                    placeholder="Nombre, correo o RUT" />
                    </div>
                    <div className="col-md-4 mb-2">
                        <div className="btn-group" role="group">
                            <button type="button" className={`btn btn-sm ${status === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setStatus('active')}>Activos</button>
                            <button type="button" className={`btn btn-sm ${status === 'archived' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setStatus('archived')}>Archivados</button>
                        </div>
                    </div>
                    <div className="col-md-4 mb-2 d-flex justify-content-end">
                        {canAdd && (
                            <ButtonGeneral onClick={() => { setErrors({}); setShowForm(true); }} title="Agregar cliente"
                                           icon="fa-user-plus" size="small" disabled={full} />
                        )}
                    </div>
                </div>

                {limits && usage && (
                    <div style={{ maxWidth: 420 }}>
                        <UsageBar label="Clientes en cartera" used={usage.clients} limit={limits.max_clients} />
                    </div>
                )}
                {full && canAdd && (
                    <div className="alert alert-info small">
                        Llegaste al límite de clientes de tu plan. Archiva clientes inactivos o mejora tu plan en Mi Estudio.
                    </div>
                )}

                <div className="table-responsive">
                    <table className="table table-hover align-middle" data-testid="clients-table">
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Cuenta</th>
                                <th className="text-center">Marcas</th>
                                <th className="text-center">Contratos</th>
                                <th>En cartera desde</th>
                                <th className="text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fetchingClients && clients.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-4"><div className="spinner-border text-primary" /></td></tr>
                            ) : clients.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center text-muted py-4">
                                        <i className="fas fa-address-book fa-3x d-block mb-3 text-secondary" />
                                        {search ? 'No hay clientes con esa búsqueda.' : 'Aún no tienes clientes en esta lista.'}
                                    </td>
                                </tr>
                            ) : clients.map((client) => (
                                <tr key={client.id}>
                                    <td>
                                        <span className="fw-bold d-block">{client.name}</span>
                                        <small className="text-muted">{client.email}{client.rut ? ` · ${client.rut}` : ''}</small>
                                    </td>
                                    <td>
                                        {client.status === 'pending' ? (
                                            <span className="badge bg-info text-dark" title="Ya tenía cuenta: debe aceptar la invitación del estudio">
                                                Pendiente de aceptación
                                            </span>
                                        ) : (
                                            <span className={`badge ${client.account_active ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                {client.account_active ? 'Activa' : 'Sin activar'}
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-center">{client.brand_applications_count ?? 0}</td>
                                    <td className="text-center">{client.contracts_count ?? 0}</td>
                                    <td><small>{formatDate(client.created_at)}</small></td>
                                    <td className="text-center">
                                        <ButtonTableAction callbackFunction={() => history.push(`/intranet/clients/${client.id}`)}
                                                           title="Ver ficha" classIcon="fa-eye" colorIcon="text-info" sizeIcon="16" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {pages > 1 && (
                    <div className="d-flex justify-content-end align-items-center gap-2">
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1}
                                onClick={() => { setPage(page - 1); load(page - 1); }}>Anterior</button>
                        <small>Página {page} de {pages}</small>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= pages}
                                onClick={() => { setPage(page + 1); load(page + 1); }}>Siguiente</button>
                    </div>
                )}
            </DefaultCard>

            <DefaultModal
                title="Agregar cliente"
                show={showForm}
                handleClose={() => setShowForm(false)}
                actionButton={<ButtonSaveForm callbackSave={handleStore} loading={fetchingStoreClient} title="Agregar" />}
            >
                <div className="row">
                    <div className="col-12 mb-3">
                        <InputField label="Correo" type="email" value={form.email} required
                                    onChange={(value) => setForm({ ...form, email: value })}
                                    onFocus={() => onFocusRemove('email')} error={fieldErrorMessage('email')} />
                    </div>
                    <div className="col-md-6 mb-3">
                        <InputField label="Nombre" value={form.first_name} required
                                    onChange={(value) => setForm({ ...form, first_name: value })}
                                    onFocus={() => onFocusRemove('first_name')} error={fieldErrorMessage('first_name')} />
                    </div>
                    <div className="col-md-6 mb-3">
                        <InputField label="Apellido paterno" value={form.last_name} required
                                    onChange={(value) => setForm({ ...form, last_name: value })}
                                    onFocus={() => onFocusRemove('last_name')} error={fieldErrorMessage('last_name')} />
                    </div>
                    <div className="col-md-6 mb-3">
                        <InputField label="Apellido materno" value={form.second_last_name}
                                    onChange={(value) => setForm({ ...form, second_last_name: value })}
                                    onFocus={() => onFocusRemove('second_last_name')} error={fieldErrorMessage('second_last_name')} />
                    </div>
                    <div className="col-12">
                        <SquareCheckbox
                            id="send_welcome"
                            name="send_welcome"
                            value="send_welcome"
                            checked={form.send_welcome}
                            onChange={(_value: string, checked: boolean) => setForm({ ...form, send_welcome: checked })}
                            label="Enviarle un correo con el acceso a su portal"
                        />
                    </div>
                </div>
            </DefaultModal>
        </div>
    );
};

export default ClientList;
